// Import d'un fichier Excel : choix du fichier et des onglets, aperçu (lignes reconnues, erreurs), puis confirmation.
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import { FileDownloadOutlined, UploadFileOutlined } from '@mui/icons-material';
import { lireClasseur } from '../../utils/excel';
import { apiError } from '../../utils/format';

/**
 * analyser(feuilles) → { colonnes, apercu: [[...]], erreurs: [texte], avertissements: [texte], resume, payload }
 *   feuilles = [{ feuille, lignes }] : onglets sélectionnés (lignes = tableau de cellules)
 * importer(payload) → Promise<message>
 */
const ImportDialog = ({ open, titre, consignes, onModele, analyser, importer, options, onClose, onDone, maxWidth = 'md' }) => {
  const [fichier, setFichier] = useState(null);
  const [feuilles, setFeuilles] = useState([]);
  const [choix, setChoix] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [survol, setSurvol] = useState(false);
  const input = useRef();
  useEffect(() => { if (open) { setFichier(null); setFeuilles([]); setChoix([]); setError(''); } }, [open]);

  const charger = async (f) => {
    if (!f) return;
    setFichier(f); setFeuilles([]); setChoix([]); setError('');
    try {
      const fs = await lireClasseur(f);
      setFeuilles(fs);
      // Par défaut : les onglets où quelque chose est reconnu (sinon le premier)
      const utiles = fs.filter((x) => { try { return !!analyser([x]).payload; } catch { return false; } }).map((x) => x.feuille);
      setChoix(utiles.length ? utiles : [fs[0]?.feuille].filter(Boolean));
    } catch (e) { setError(e.message); }
  };
  let res = null;
  try { res = choix.length ? analyser(feuilles.filter((x) => choix.includes(x.feuille))) : null; } catch (e) { res = { colonnes: [], apercu: [], erreurs: [e.message], resume: '', payload: null }; }
  const basculer = (n) => setChoix((c) => (c.includes(n) ? c.filter((x) => x !== n) : [...c, n]));

  const go = async () => {
    setBusy(true); setError('');
    try { onDone(await importer(res.payload)); } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={maxWidth}>
      <DialogTitle>{titre}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>{consignes}</Box>
        {onModele && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button variant="outlined" startIcon={<FileDownloadOutlined />} onClick={onModele}>Télécharger le modèle Excel</Button>
          </Box>
        )}
        <Box onClick={() => input.current?.click()} onDragOver={(e) => { e.preventDefault(); setSurvol(true); }} onDragLeave={() => setSurvol(false)}
          onDrop={(e) => { e.preventDefault(); setSurvol(false); charger(e.dataTransfer.files[0]); }}
          sx={{ border: '2px dashed', borderColor: survol ? 'primary.main' : 'divider', borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer', bgcolor: survol ? 'action.hover' : undefined }}>
          <UploadFileOutlined sx={{ fontSize: 34, color: 'text.secondary' }} />
          <Typography sx={{ fontWeight: 600 }}>{fichier ? fichier.name : 'Choisir ou déposer le fichier Excel (.xlsx)'}</Typography>
          {fichier && <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Cliquez pour choisir un autre fichier</Typography>}
          <input ref={input} type="file" hidden accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            aria-label="Fichier Excel" onChange={(e) => { charger(e.target.files[0]); e.target.value = ''; }} />
        </Box>
        {feuilles.length > 1 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>Onglets à importer</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {feuilles.map((x) => (
                <Chip key={x.feuille} label={x.feuille} onClick={() => basculer(x.feuille)}
                  color={choix.includes(x.feuille) ? 'primary' : 'default'} variant={choix.includes(x.feuille) ? 'filled' : 'outlined'} />
              ))}
            </Box>
          </Box>
        )}
        {options && <Box sx={{ mt: 2 }}>{options}</Box>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {res && (
          <Box sx={{ mt: 2 }}>
            {res.erreurs.length > 0 && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                <b>{res.erreurs.length} ligne(s) ignorée(s) :</b>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{res.erreurs.slice(0, 12).map((e) => <li key={e}>{e}</li>)}</Box>
                {res.erreurs.length > 12 && <span>… et {res.erreurs.length - 12} autre(s).</span>}
              </Alert>
            )}
            {res.avertissements?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{res.avertissements.slice(0, 10).map((e) => <li key={e}>{e}</li>)}</Box>
              </Alert>
            )}
            <Typography sx={{ fontWeight: 700, mb: 1 }}>{res.resume}</Typography>
            {res.contenu}
            {res.apercu.length > 0 && (
              <TableContainer sx={{ maxHeight: 320, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow>{res.colonnes.map((c) => <TableCell key={c} sx={{ whiteSpace: 'nowrap' }}>{c}</TableCell>)}</TableRow></TableHead>
                  <TableBody>
                    {res.apercu.slice(0, 200).map((l, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <TableRow key={i}>{l.map((c, j) => <TableCell key={j} sx={{ fontSize: 13 }}>{c}</TableCell>)}</TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={busy || !res?.payload} onClick={go}>Importer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
